import React from 'react';
import clsx from 'clsx';
import {useMatches} from '@remix-run/react';

//components
import SanityRichText from '~/components/modules/SanityRichText';
import {Link} from 'components/Link';

//domains
import {colourToClassName} from 'domains/sanity';
import type {Announcement} from '~/domains/modules';
import type {LatestBlog} from '~/domains/root';

const BAR_BASE =
  'relative py-1.5 md:py-2.5 px-3 text-center announcement-bar min-h-[26px] flex flex-col items-center justify-center overflow-hidden ' +
  'before:pointer-events-none before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/20 before:via-transparent before:to-black/10';

const ROTATE_MS = 5000;

function blogTo(blog: LatestBlog): string {
  return blog.categorySlug
    ? `/blogs/${blog.categorySlug}/${blog.slug}`
    : '/blogs';
}

/**
 * Top announcement bar. It rotates through the newest blog posts (up to 5),
 * showing one title every 5s and looping — each title links to its article.
 * If there are no blog posts it falls back to the CMS announcement message
 * (the previous behaviour).
 */
export default function AnnouncementBar() {
  const [root] = useMatches();
  const announcement = root?.data?.announcement as Announcement;
  const IPAddressLocation = root?.data.ipAddressLocation as string;
  const latestBlogs = root?.data?.latestBlogs as LatestBlog[] | undefined;

  const blogs = React.useMemo(
    () => (latestBlogs ?? []).filter((b) => b?.blog_title && b?.slug),
    [latestBlogs],
  );

  const [index, setIndex] = React.useState(0);

  // Advance one post every ROTATE_MS, looping. First paint (SSR + hydration)
  // shows blogs[0]; the interval is client-only, so there's no hydration
  // mismatch. Reset when the set changes so we never index past the end.
  React.useEffect(() => {
    setIndex(0);
    if (blogs.length <= 1) return;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % blogs.length),
      ROTATE_MS,
    );
    return () => clearInterval(id);
  }, [blogs.length]);

  const current = blogs.length ? blogs[index % blogs.length] : undefined;

  // Rotate through the latest blog posts, linking each to its article. The
  // key change on each tick replays the fade-in for a gentle cross-fade.
  if (current) {
    return (
      <div className={clsx(BAR_BASE, 'bg-hk-orange text-black')}>
        <Link
          key={current.slug}
          to={blogTo(current)}
          className="fadeIn relative inline-block text-xs font-bold tracking-wide underline decoration-1 underline-offset-2 transition-opacity duration-150 hover:opacity-80 md:text-[15px]"
        >
          {current.blog_title}
        </Link>
      </div>
    );
  }

  // Fallback: the CMS announcement message (previous behaviour).
  if (!announcement?.enabled) {
    return <></>;
  }

  const localMessage = announcement?.messages.find(
    (a) => a.country === IPAddressLocation,
  );
  const globalMessage = announcement?.messages.find(
    (a) => a.country === 'GLOBAL',
  );

  return (
    <div
      className={clsx(
        BAR_BASE,
        localMessage
          ? colourToClassName(localMessage.bg_colour.title, 'bg')
          : globalMessage
          ? colourToClassName(globalMessage.bg_colour.title, 'bg')
          : colourToClassName('Orange', 'bg'),
        localMessage
          ? colourToClassName(localMessage.text_colour.title, 'text')
          : globalMessage
          ? colourToClassName(globalMessage.text_colour.title, 'text')
          : colourToClassName('Orange', 'text'),
      )}
    >
      <div className="relative text-xs font-medium tracking-wide md:text-[15px]">
        {localMessage ? (
          <SanityRichText richtext={localMessage.message} className="min-h-0" />
        ) : globalMessage ? (
          <SanityRichText richtext={globalMessage.message} className="min-h-0" />
        ) : (
          'OVER HKD$500 FREE SHIPPING TO HONG KONG & MACAU'
        )}
      </div>
    </div>
  );
}
